/**
 * Klartext einer Verlaufszeile: was übertragen wurde und was Channex bei der Rückprüfung dazu
 * gesagt hat. Ohne React, damit die Wortwahl prüfbar bleibt.
 */
import type { AriPushHistoryItem } from '@/shared/api';
import type { AriInitialValues } from '../ari-edit/helpers';

const joinGerman = (parts: string[]) =>
  parts.length < 2 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} und ${parts.at(-1)}`;

export function pushHeadline(item: AriPushHistoryItem): string {
  if (item.stopSell === true) return 'Verkaufsstopp gesetzt';
  if (item.available === false) return 'Blockiert';
  const changed: string[] = [];
  if (item.rate != null) changed.push('Preis');
  if (item.minStay != null || item.maxStay != null) changed.push('Aufenthaltsdauer');
  if (item.closedToArrival != null || item.closedToDeparture != null) changed.push('An-/Abreise');
  if (item.stopSell === false) changed.push('Verkaufsstopp');
  const text = changed.length > 0 ? `${joinGerman(changed)} geändert` : null;
  if (item.available === true) return text ? `Freigegeben · ${text}` : 'Freigegeben';
  return text ?? 'Übertragen';
}

export type ReadbackTone = 'confirmed' | 'drift' | 'unchecked';

/** Die Gründe kommen als technischer Text vom Sender; die Zeile nennt sie in Worten des Betriebs. */
const SKIPPED_TEXT: Record<string, string> = {
  'auto-readback disabled': 'Die Rückprüfung ist ausgeschaltet.',
  'nothing pushable': 'Es gab nichts zurückzuprüfen.',
  'channex returned no data': 'Channex hat keine Daten zurückgeliefert.',
};

/**
 * Gewollt ist nicht wirksam (FRONTEND_GUIDELINES §11): bestätigt ist eine Übertragung erst, wenn
 * die Rückprüfung jeden ihrer Tage verglichen hat und keiner abweicht. Fehlende Tage zählen nicht
 * als stimmig.
 */
export function readbackStatus(item: AriPushHistoryItem): {
  label: string;
  tone: ReadbackTone;
  detail?: string;
} {
  const readback = item.readback;
  if (!readback) return { label: 'Gesendet', tone: 'unchecked' };
  if (!readback.performed) {
    return {
      label: 'Gesendet',
      tone: 'unchecked',
      detail: readback.skippedReason
        ? (SKIPPED_TEXT[readback.skippedReason] ?? 'Die Rückprüfung ist gescheitert.')
        : 'Nicht zurückgeprüft.',
    };
  }
  if (readback.driftCount > 0) {
    return {
      label: 'Abweichung bei der Rückprüfung',
      tone: 'drift',
      detail: [
        `${readback.driftCount} von ${readback.comparedDays} Tagen weichen ab`,
        ...readback.driftSummary,
      ].join('\n'),
    };
  }
  if (item.readbackExpectedDays > 0 && readback.comparedDays >= item.readbackExpectedDays) {
    return { label: 'Von Channex bestätigt', tone: 'confirmed' };
  }
  return {
    label: 'Nicht vollständig bestätigt',
    tone: 'unchecked',
    detail: `Channex hat ${readback.comparedDays} von ${item.readbackExpectedDays} Tagen zurückgemeldet.`,
  };
}

export function nightsText(count: number) {
  return count === 1 ? '1 Nacht' : `${count} Nächte`;
}

/** „Erneut": derselbe Zeitraum mit denselben Werten zurück in die Bearbeitung. */
export function reapplyValues(item: AriPushHistoryItem): AriInitialValues {
  return {
    from: item.dateFrom,
    to: item.dateTo,
    rate: item.rate,
    available: item.available,
    minStay: item.minStay,
    maxStay: item.maxStay,
    closedToArrival: item.closedToArrival,
    closedToDeparture: item.closedToDeparture,
    stopSell: item.stopSell,
  };
}
